import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ReportBackendService } from 'src/app/services/report-backend.service';
import { CustomReportCriteria } from '../models/custom-report-criteria.model';
import { CustomReportDisplayField } from '../models/custom-report-display-field.model';
import { CustomReportOperator } from '../models/custom-report-operator.model';
import { CustomReportTable } from '../models/custom-report-table.model';
import { CustomReport } from '../models/custom-report.model';
import { PreCannedReport } from '../models/pre-canned-report.model';
import { ReportHistory } from '../models/report-history.model';
import { ReportSchedule } from '../models/report-schedule.model';
import { FileUtility } from '../utility/file.utility';
import { Logout } from './auth.action';
import { StartLoading, StopLoading } from './main.action';
import {
	ClearReportHTML,
	DeleteCustomReport,
	DeleteReportSchedule,
	DownloadReport,
	DownloadScheduledReports,
	GetAllReports,
	GetCustomReport,
	GetCustomReports,
	GetCustomReportTables,
	GetPreCannedReports,
	GetReportHistory,
	GetReportHTML,
	GetReportSchedule,
	GetReportSchedules,
	SaveCustomReport,
	SaveReportSchedule,
} from './report.action';
import { ReportStateModel } from './report.model';

export const DEFAULT_STATE: ReportStateModel = {
	customReports: null,
	preCannedReports: null,
	reportSchedules: null,
	reportHistory: null,
	reportHTML: null,
	reportName: null,
	selectedReportSchedule: null,
	allReports: null,
	selectedCustomReport: null,
	selectedCustomReportDisplayFields: null,
	selectedCustomReportCriteria: null,
	customReportTables: null,
	customReportStringOperators: null,
	customReportDateOperators: null,
};

@State<ReportStateModel>({
	name: 'report',
	defaults: DEFAULT_STATE,
})
@Injectable()
export class ReportState {
	private reportBackend = inject(ReportBackendService);

	@Selector()
	static reportState(state: ReportStateModel): ReportStateModel {
		return state;
	}

	@Selector()
	static customReports(state: ReportStateModel): CustomReport[] {
		return state.customReports;
	}

	@Selector()
	static preCannedReports(state: ReportStateModel): PreCannedReport[] {
		return state.preCannedReports;
	}

	@Selector()
	static reportSchedules(state: ReportStateModel): ReportSchedule[] {
		return state.reportSchedules;
	}

	@Selector()
	static reportHistory(state: ReportStateModel): ReportHistory[] {
		return state.reportHistory;
	}

	@Selector()
	static reportHTML(state: ReportStateModel): string {
		return state.reportHTML;
	}

	@Selector()
	static reportName(state: ReportStateModel): string {
		return state.reportName;
	}

	@Selector()
	static selectedReportSchedule(state: ReportStateModel): ReportSchedule {
		return state.selectedReportSchedule;
	}

	@Selector()
	static allReports(state: ReportStateModel): PreCannedReport[] {
		return state.allReports;
	}

	@Selector()
	static selectedCustomReport(state: ReportStateModel): CustomReport {
		return state.selectedCustomReport;
	}

	@Selector()
	static selectedCustomReportDisplayFields(
		state: ReportStateModel
	): CustomReportDisplayField[] {
		return state.selectedCustomReportDisplayFields;
	}

	@Selector()
	static selectedCustomReportCriteria(
		state: ReportStateModel
	): CustomReportCriteria[] {
		return state.selectedCustomReportCriteria;
	}

	@Selector()
	static customReportTables(state: ReportStateModel): CustomReportTable[] {
		return state.customReportTables;
	}

	@Selector()
	static customReportStringOperators(
		state: ReportStateModel
	): CustomReportOperator[] {
		return state.customReportStringOperators;
	}

	@Selector()
	static customReportDateOperators(
		state: ReportStateModel
	): CustomReportOperator[] {
		return state.customReportDateOperators;
	}

	@Action(GetCustomReports)
	getCustomReports(
		ctx: StateContext<ReportStateModel>,
		_action: GetCustomReports
	) {
		ctx.dispatch([new ClearReportHTML()]);
		return this.reportBackend
			.getCustomReports()
			.pipe(tap((reports) => ctx.patchState({ customReports: reports })));
	}

	@Action(GetCustomReport)
	getCustomReport(
		ctx: StateContext<ReportStateModel>,
		action: GetCustomReport
	) {
		ctx.dispatch([new StartLoading()]);
		ctx.patchState({
			selectedCustomReport: null,
			selectedCustomReportDisplayFields: null,
			selectedCustomReportCriteria: null,
		});
		return this.reportBackend.getCustomReport(action.id).pipe(
			tap((response) => {
				ctx.patchState({
					selectedCustomReport: CustomReport.fromJSON(
						response.report
					),
					selectedCustomReportDisplayFields: response.fields.map(
						(json) => CustomReportDisplayField.fromJSON(json)
					),
					selectedCustomReportCriteria: response.criteria.map(
						(json) => CustomReportCriteria.fromJSON(json)
					),
				});
				ctx.dispatch([new StopLoading()]);
			})
		);
	}

	@Action(GetCustomReportTables)
	getCustomReportTables(
		ctx: StateContext<ReportStateModel>,
		_action: GetCustomReportTables
	) {
		return this.reportBackend.getCustomReportTables().pipe(
			tap((response) =>
				ctx.patchState({
					customReportTables: response.tables.map((json) =>
						CustomReportTable.fromJSON(json)
					),
					customReportStringOperators: response.stringOperators.map(
						(json) => CustomReportOperator.fromJSON(json)
					),
					customReportDateOperators: response.dateOperators.map(
						(json) => CustomReportOperator.fromJSON(json)
					),
				})
			)
		);
	}

	@Action(DeleteCustomReport)
	deleteCustomReport(
		ctx: StateContext<ReportStateModel>,
		action: DeleteCustomReport
	) {
		return this.reportBackend
			.deleteCustomReport(action.id)
			.pipe(tap(() => ctx.dispatch([new GetCustomReports()])));
	}

	@Action(SaveCustomReport)
	saveCustomReport(
		ctx: StateContext<ReportStateModel>,
		action: SaveCustomReport
	) {
		return this.reportBackend
			.saveCustomReport(action.saveData)
			.pipe(tap(() => ctx.dispatch([new GetCustomReports()])));
	}

	@Action(GetPreCannedReports)
	getPreCannedReports(
		ctx: StateContext<ReportStateModel>,
		_action: GetPreCannedReports
	) {
		ctx.dispatch([new ClearReportHTML()]);
		return this.reportBackend
			.getPreCannedReports()
			.pipe(
				tap((reports) => ctx.patchState({ preCannedReports: reports }))
			);
	}

	@Action(GetAllReports)
	getAllReports(ctx: StateContext<ReportStateModel>, _action: GetAllReports) {
		return this.reportBackend
			.getAllReports()
			.pipe(tap((reports) => ctx.patchState({ allReports: reports })));
	}

	@Action(ClearReportHTML)
	clearReportHTML(
		ctx: StateContext<ReportStateModel>,
		_action: ClearReportHTML
	) {
		ctx.patchState({ reportHTML: null, reportName: null });
	}

	@Action(GetReportHTML)
	getReportHTML(ctx: StateContext<ReportStateModel>, action: GetReportHTML) {
		ctx.dispatch([new StartLoading()]);
		return this.reportBackend.getReportHTML(action.id).pipe(
			tap((response) => {
				ctx.patchState({
					reportHTML: response.html,
					reportName: response.name,
				});
				ctx.dispatch([new StopLoading()]);
			}),
			catchError((error) => {
				ctx.dispatch([new StopLoading()]);
				return throwError(() => error);
			})
		);
	}

	@Action(DownloadReport)
	downloadReport(
		ctx: StateContext<ReportStateModel>,
		action: DownloadReport
	) {
		ctx.dispatch([new StartLoading()]);
		return this.reportBackend.downloadReport(action.id, action.type).pipe(
			tap((blob) => {
				FileUtility.open(blob, action.name + '.' + action.type);
				ctx.dispatch([new StopLoading()]);
			}),
			catchError((error) => {
				ctx.dispatch([new StopLoading()]);
				return throwError(() => error);
			})
		);
	}

	@Action(GetReportSchedules)
	getReportSchedules(
		ctx: StateContext<ReportStateModel>,
		_action: GetReportSchedules
	) {
		return this.reportBackend
			.getReportSchedules()
			.pipe(
				tap((schedules) =>
					ctx.patchState({ reportSchedules: schedules })
				)
			);
	}

	@Action(GetReportSchedule)
	getReportSchedule(
		ctx: StateContext<ReportStateModel>,
		action: GetReportSchedule
	) {
		ctx.dispatch([new StartLoading()]);
		ctx.patchState({ selectedReportSchedule: null });
		return this.reportBackend.getReportSchedule(action.id).pipe(
			tap((schedule) => {
				ctx.patchState({ selectedReportSchedule: schedule });
				ctx.dispatch([new StopLoading()]);
			})
		);
	}

	@Action(SaveReportSchedule)
	saveReportSchedule(
		ctx: StateContext<ReportStateModel>,
		action: SaveReportSchedule
	) {
		ctx.dispatch([new StartLoading()]);
		return this.reportBackend.saveReportSchedule(action.schedule).pipe(
			tap(() => {
				ctx.patchState({ selectedReportSchedule: null });
				ctx.dispatch([new StopLoading()]);
			})
		);
	}

	@Action(DeleteReportSchedule)
	deleteReportSchedule(
		ctx: StateContext<ReportStateModel>,
		action: DeleteReportSchedule
	) {
		return this.reportBackend
			.deleteReportSchedule(action.id)
			.pipe(tap(() => ctx.dispatch([new GetReportSchedules()])));
	}

	@Action(GetReportHistory)
	getReportHistory(
		ctx: StateContext<ReportStateModel>,
		_action: GetReportHistory
	) {
		return this.reportBackend
			.getReportHistory()
			.pipe(tap((history) => ctx.patchState({ reportHistory: history })));
	}

	@Action(DownloadScheduledReports)
	downloadScheduledReports(
		ctx: StateContext<ReportStateModel>,
		action: DownloadScheduledReports
	) {
		ctx.dispatch([new StartLoading()]);
		return this.reportBackend.downloadScheduledReports(action.report).pipe(
			tap((doc) => {
				doc.open();
				ctx.dispatch([new StopLoading()]);
			}),
			catchError((error) => {
				ctx.dispatch([new StopLoading()]);
				return throwError(() => error);
			})
		);
	}

	@Action(Logout)
	logout(ctx: StateContext<ReportStateModel>, _action: Logout) {
		ctx.patchState(DEFAULT_STATE);
	}
}
